const THEME_IDS = Object.freeze({
  COMMON: 'common',
  BLUE: 'blue',
})

const AVAILABLE_THEME_VARIABLES = Object.freeze({
  PRIMARY_COLOR: '--primary-color',
  BASE_FOCUS_COLOR: '--base-focus-color',
  LINK_COLOR: '--link-color',
  SUCCESS_COLOR: '--success-color',
})

const KNOWN_PAGES = Object.freeze({
  LOGIN_ID: 'login-id',
  LOGIN_PASSWORD: 'login-password',
  SIGNUP_ID: 'signup-id',
  SIGNUP_PASSWORD: 'signup-password',
  SIGNUP: 'signup',
  RESET_PASSWORD: 'reset-password',
  RESET_PASSWORD_REQUEST: 'reset-password-request',
  RESET_PASSWORD_EMAIL: 'reset-password-email',
  RESET_PASSWORD_RESET_PASSWORD: 'reset-password',
  RESET_PASSWORD_SUCCESS: 'reset-password-success',
  RESET_PASSWORD_ERROR: 'reset-password-error',
  PASSKEY_ENROLLMENT: 'passkey-enrollment',
})

const config = {
  defaultGroupName: 'Norstella',
  applicationGroups: [
    {
      name: 'Norstella',
      themeId: THEME_IDS.COMMON,
      logoUrl: `${process.env.SSO_URL || ''}/logos/norstella/norstella.svg`,
      logoBackground: '#001E43',
      signup: {
        full: {
          isConsentRequired: false,
          terms:
              `<label for="consent" class="app-terms">
                Your personal information will be used in accordance with our <a href="https://www.norstella.com/global-privacy-notice/" target="_blank" rel="noopener nofollow">privacy notice</a>.
                We may contact you with updates, relevant promotions and information about our products. At any time, 
                you can unsubscribe or manage the types of communication you receive by following the links in any messages 
                we send you.
            </label>`,
        },
      },
      resetPassword: {
        clientServicesSection: '',
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/norstella/norstella.png`,
        email_footer_team_signature: '{{resolved_application_name}}',
        contactClientServiceContent: '',
        legalText: '',
        copyrightText: 'Copyright &copy;&nbsp;Norstella',

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        reset_email_link_text: 'Reset password',

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your API password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        blocked_account_email_title: `Blocked Access Attempt`,
        blocked_account_email_body:
            `<p
            >Dear {{ user.name }},
            </p>
            <p>
                We&nbsp;detected suspicious activity from the&nbsp;ip:: <strong>{{ user.source_ip }}</strong> from
                <strong>{{ user.city }}</strong>, <strong>{{ user.country }}</strong>.
            </p>
            `,
      },
      verifyFooter: {
        text: '',
        linkText: '',
        linkUrl: '',
      },
      errorFooter: {
        text: '',
        linkText: '',
        linkUrl: '',
      },
      fedSignupErrorFooter: {
        text: '',
        linkText: '',
        linkUrl: '',
      },
      contacts: {
        phones: [''],
        email: '',
        link: undefined,
      },
    },
    {
      name: 'Citeline',
      themeId: THEME_IDS.COMMON,
      logoUrl: `${process.env.SSO_URL || ''}/logos/citiline-logo-dual-color.svg`,
      logoBackground: '#14123b',
      signup: {
        trial: {
          terms:
              `<p>During and after your free trial, Citeline will communicate with you by email about your trial and similar products and services that may be of interest to you.</p>
             <p>Your information will be used in accordance with our <a href="https://www.norstella.com/privacy-notice/" target="_blank" rel="noopener nofollow">Privacy notice</a> and at&nbsp;any time
              you can unsubscribe from receiving communications or&nbsp;manage the types of&nbsp;communication you receive by&nbsp;following the links in&nbsp;any emails we&nbsp;send you.</p>`,
        },
      },
      resetPassword: {
        clientServicesSection:
            `<p>If&nbsp;you need further assistance contact 
                <a href="https://www.citeline.com/en/contact-us/" target="_blank" rel="noopener nofollow">
                  Customer Support
                </a>
              </p>`,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/citiline-logo-dual-color-w212.png`,
        email_footer_team_signature: '{{resolved_application_name}}',
        contactClientServiceContent:
            `<p
                    >If&nbsp;you did not make this request, or&nbsp;if&nbsp;you require further assistance, please contact
                    <a href="mailto:clientservices@citeline.com"
                    >Customer Support.
                    </a>
                  </p>`,
        legalText:
            `Citeline is&nbsp;owned by&nbsp;Caerus
                    US&nbsp;1, Inc.&nbsp;a&nbsp;Delaware
                    corporation with company number 6591595 (&#8220;Citeline&#8221;).
                    Citeline&rsquo;s
                    principal place of&nbsp;business is&nbsp;located at&nbsp;
                    One Pennsylvania Plaza #2505,
                    New York, NY 10119.`,
        copyrightText: `Copyright &copy;&nbsp;Citeline, a Norstella company`,

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        reset_email_link_text: 'Reset password',

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your API password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        blocked_account_email_title: `Blocked Access Attempt`,
        blocked_account_email_body:
            `<p
                      >Dear {{ user.name }},
                      </p>
                      <p>
                          We&nbsp;detected suspicious activity from the&nbsp;ip:: <strong>{{ user.source_ip }}</strong> from
                          <strong>{{ user.city }}</strong>, <strong>{{ user.country }}</strong>.
                      </p>
                      <p
                      >If&nbsp;that is&nbsp;your&nbsp;IP address please contact
                          <a href="mailto:clientservices@citeline.com"
                          >Customer Support.
                          </a> Otherwise disregard this email.
                      </p>`,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Support',
        linkUrl: 'https://www.citeline.com/en/contact-us/',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        linkText: 'Customer Support',
        linkUrl: 'https://www.citeline.com/en/contact-us/',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Support',
        linkUrl: 'https://www.citeline.com/en/contact-us/',
      },
      contacts: {
        phones: [
          'Americas: +1 888-670- 8900; +1 908-547- 2200',
          'EMEA: +44 (20) 337 73737',
          'APAC: +61 2 8705 6907',
        ],
        email: 'clientservices@citeline.com',
      },
    },
    {
      name: 'Tala',
      themeId: THEME_IDS.COMMON,
      logoUrl: `${process.env.SSO_URL || ''}/logos/evaluate-norstella-logo.svg`,
      logoBackground: '#352945',
      resetPassword: {
        emailDescription: 'If you are a subscriber, an email containing a link to set up your password will be sent to the email address we hold for you.',
        clientServicesSection:
            `<p>If&nbsp;you need further assistance contact 
            <a href="https://app.evaluate.com/ux/help/contact_us.html" target="_blank" rel="noopener nofollow">
              Customer Success.
            </a>
          </p>`,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/evaluate-norstella-logo-w212.png`,
        email_footer_team_signature: '{{resolved_application_name}}',
        contactClientServiceContent:
            `<p
                    >If&nbsp;you did not make this request, or&nbsp;if&nbsp;you require further assistance, please contact
                    <a href="https://app.evaluate.com/ux/help/contact_us.html" target="_blank" rel="noopener nofollow"
                    >Customer Success.
                    </a>
                  </p>`,
        legalText: '',
        copyrightText: '',

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        reset_email_link_text: 'Reset password',

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your API password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        blocked_account_email_title: 'Blocked Access Attempt',
        blocked_account_email_body:
            `<p
                      >Dear {{ user.name }},
                      </p>
                      <p>
                          We&nbsp;detected suspicious activity from the&nbsp;ip:: <strong>{{ user.source_ip }}</strong> from
                          <strong>{{ user.city }}</strong>, <strong>{{ user.country }}</strong>.
                      </p>
                      <p
                      >If&nbsp;that is&nbsp;your&nbsp;IP address please contact
                          <a href="https://app.evaluate.com/ux/help/contact_us.html" target="_blank" rel="noopener nofollow"
                          >Customer Success.
                          </a> Otherwise disregard this email.
                      </p>`,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Success',
        linkUrl: 'https://app.evaluate.com/ux/help/contact_us.html',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        linkText: 'Customer Success',
        linkUrl: 'https://app.evaluate.com/ux/help/contact_us.html',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Success',
        linkUrl: 'https://app.evaluate.com/ux/help/contact_us.html',
      },
      contacts: {
        phones: [
          'Evaluate HQ: 44-(0)20-7377-0800',
          'Evaluate Americas: +1-617-573-9450',
          'Evaluate APAC: +81-80-1164-4754',
        ],
        email: 'info@evaluate.com',
      },
    },
    {
      name: 'Evaluate',
      themeId: THEME_IDS.COMMON,
      logoUrl: `${process.env.SSO_URL || ''}/logos/evaluate-norstella-logo.svg`,
      logoBackground: '#352945',
      resetPassword: {
        emailDescription: 'If you are a subscriber, an email containing a link to set up your password will be sent to the email address we hold for you.',
        clientServicesSection:
            `<p>If&nbsp;you need further assistance contact 
                <a href = "mailto:clientservices@evaluate.com">Client Services</a>.
          </p>`,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/evaluate-norstella-logo-w212.png`,
        email_footer_team_signature: 'Evaluate',
        contactClientServiceContent:
            `<p
                    >Please&nbsp;let us know if you require any&nbsp;further&nbsp;assistance by contacting Evaluate
                        <a href = "mailto:clientservices@evaluate.com">Client Services</a>.
                  </p>`,
        legalText: ``,
        copyrightText: `&copy;&nbsp;\${CURRENT_YEAR}&nbsp;Evaluate, a Norstella Company`,

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
                    >Your&nbsp;Evaluate subscription is now active. You will need to set a password when you first log in.
                    </p>
                    <p
                    >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
                  </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
          >Your&nbsp;Evaluate subscription is now active. You will need to set a password when you first log in.
          </p>
          <p
          >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
        </p>`,

        reset_email_link_text: 'Reset password',
        reset_email_contact_client_service_content: `<p
                    >If&nbsp;you did not make this request, or&nbsp;if&nbsp;you require further assistance, please contact
                    <a href = "mailto:clientservices@evaluate.com">Client Services</a>.
                  </p>`,

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
                  >Dear {{ user.name }},
                  </p>
                  <p
                  >We&nbsp;have received a&nbsp;request to&nbsp;reset your Evaluate password.
                  </p>
                  <p
                  >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
                </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Your&nbsp;Evaluate subscription is now active. You will need to set a password when you first log in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
          </p>`,

        blocked_account_email_title: `Blocked Access Attempt`,
        blocked_account_email_body:
            `<p
                      >Dear {{ user.name }},
                      </p>
                      <p>
                          We&nbsp;detected suspicious activity from the&nbsp;ip:: <strong>{{ user.source_ip }}</strong> from
                          <strong>{{ user.city }}</strong>, <strong>{{ user.country }}</strong>.
                      </p>
                      <p
                      >If&nbsp;that is&nbsp;your&nbsp;IP address please contact
                      <a href = "mailto:clientservices@evaluate.com">Client Services</a>. Otherwise disregard this email.
                      </p>`,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        emailText: 'Client Services',
        emailAddress: 'clientservices@evaluate.com',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        emailText: 'Client Services',
        emailAddress: 'clientservices@evaluate.com',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        emailText: 'Client Services',
        emailAddress: 'clientservices@evaluate.com',
      },
      contacts: {
        phones: [
          'Evaluate HQ: 44-(0)20-7377-0800',
          'Evaluate Americas: +1-617-573-9450',
          'Evaluate APAC: +81-80-1164-4754',
        ],
        email: 'info@evaluate.com',
      },
    },
    {
      name: 'NorstellaLinQ',
      themeId: THEME_IDS.COMMON,
      logoUrl: `${process.env.SSO_URL || ''}/logos/norstellaLinQ/norstella-linq.svg`,
      logoBackground: '#001E43',
      resetPassword: {
        emailDescription: 'If you are a subscriber, an email containing a link to set up your password will be sent to the email address we hold for you.',
        clientServicesSection:
            `<p>If&nbsp;you need further assistance contact 
            <a href = "mailto:clientservices@evaluate.com">Client Services</a>.
          </p>`,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/norstellaLinQ/norstella-linq.png`,
        email_footer_team_signature: 'Evaluate',
        contactClientServiceContent:
            `<p
                    >Please&nbsp;let us know if you require any&nbsp;further&nbsp;assistance by contacting Evaluate
                    <a href = "mailto:clientservices@evaluate.com">Client Services</a>.
                  </p>`,
        legalText: '',
        copyrightText: `&copy;&nbsp;\${CURRENT_YEAR}&nbsp;Evaluate, a Norstella Company`,

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
                    >Your&nbsp;Evaluate subscription is now active. You will need to set a password when you first log in.
                    </p>
                    <p
                    >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
                  </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
          >Your&nbsp;Evaluate subscription is now active. You will need to set a password when you first log in.
          </p>
          <p
          >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
        </p>`,

        reset_email_link_text: 'Reset password',
        reset_email_contact_client_service_content: `<p
                    >If&nbsp;you did not make this request, or&nbsp;if&nbsp;you require further assistance, please contact
                    <a href = "mailto:clientservices@evaluate.com">Client Services</a>.
                  </p>`,

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
                  >Dear {{ user.name }},
                  </p>
                  <p
                  >We&nbsp;have received a&nbsp;request to&nbsp;reset your Evaluate password.
                  </p>
                  <p
                  >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
                </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Your&nbsp;Evaluate subscription is now active. You will need to set a password when you first log in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
          </p>`,

        blocked_account_email_title: `Blocked Access Attempt`,
        blocked_account_email_body:
            `<p
                      >Dear {{ user.name }},
                      </p>
                      <p>
                          We&nbsp;detected suspicious activity from the&nbsp;ip:: <strong>{{ user.source_ip }}</strong> from
                          <strong>{{ user.city }}</strong>, <strong>{{ user.country }}</strong>.
                      </p>
                      <p
                      >If&nbsp;that is&nbsp;your&nbsp;IP address please contact
                      <a href = "mailto:clientservices@evaluate.com">Client Services</a>. Otherwise disregard this email.
                      </p>`,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        emailText: 'Client Services',
        emailAddress: 'clientservices@evaluate.com',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        emailText: 'Client Services',
        emailAddress: 'clientservices@evaluate.com',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        emailText: 'Client Services',
        emailAddress: 'clientservices@evaluate.com',
      },
      contacts: {
        phones: [
          'Evaluate HQ: 44-(0)20-7377-0800',
          'Evaluate Americas: +1-617-573-9450',
          'Evaluate APAC: +81-80-1164-4754',
        ],
        email: 'info@evaluate.com',
      },
    },
    {
      name: 'Clinical',
      themeId: THEME_IDS.COMMON,
      logoUrl: `${process.env.SSO_URL || ''}/logos/citiline-logo-dual-color.svg`,
      logoBackground: '#14123b',
      resetPassword: {
        clientServicesSection:
            `<p>If&nbsp;you need further assistance contact 
                <a href="#" target="_blank" rel="noopener nofollow">
                  Client Services
                </a>
              </p>`,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/citiline-logo-dual-color-w212.png`,
        email_footer_team_signature: '{{resolved_application_name}}',
        contactClientServiceContent:
            `<p
                    >If&nbsp;you did not make this request, or&nbsp;if&nbsp;you require further assistance, please contact
                    <a href="#"
                    >Client Services.
                    </a>
                  </p>`,
        legalText:
            `Placeholder Clinical`,
        copyrightText: `Placeholder Copyright &copy;&nbsp;`,

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        reset_email_link_text: 'Reset password',

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your API password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        blocked_account_email_title: `Blocked Access Attempt`,
        blocked_account_email_body:
            `<p
                      >Dear {{ user.name }},
                      </p>
                      <p>
                          We&nbsp;detected suspicious activity from the&nbsp;ip:: <strong>{{ user.source_ip }}</strong> from
                          <strong>{{ user.city }}</strong>, <strong>{{ user.country }}</strong>.
                      </p>
                      <p
                      >If&nbsp;that is&nbsp;your&nbsp;IP address please contact
                          <a href="#"
                          >Client Services.
                          </a> Otherwise disregard this email.
                      </p>`,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Client Services',
        linkUrl: 'https://www.citeline.com/en/contact-us/',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        linkText: 'Customer Support',
        linkUrl: 'https://www.citeline.com/en/contact-us/',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Success',
        linkUrl: 'https://app.evaluate.com/ux/help/contact_us.html',
      },
      contacts: {
        phones: [
          'Americas: +1 888-670- 8900; +1 908-547- 2200',
          'EMEA: +44 (20) 337 73737',
          'APAC: +61 2 8705 6907',
        ],
        email: 'clientservices@citeline.com',
      },
    },
    {
      name: 'Stratintel',
      themeId: THEME_IDS.COMMON,
      logoUrl: `${process.env.SSO_URL || ''}/logos/evaluate-norstella-logo.svg`,
      logoBackground: '#352945',
      resetPassword: {
        emailDescription: 'If you are a subscriber, an email containing a link to set up your password will be sent to the email address we hold for you.',
        clientServicesSection:
            `<p>If&nbsp;you need further assistance contact 
            <a href="https://app.evaluate.com/ux/help/contact_us.html" target="_blank" rel="noopener nofollow">
              Customer Success.
            </a>
          </p>`,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/evaluate-norstella-logo-w212.png`,
        email_footer_team_signature: '{{resolved_application_name}}',
        contactClientServiceContent:
            `<p
                    >If&nbsp;you did not make this request, or&nbsp;if&nbsp;you require further assistance, please contact
                    <a href="https://app.evaluate.com/ux/help/contact_us.html" target="_blank" rel="noopener nofollow"
                    >Customer Success.
                    </a>
                  </p>`,
        legalText: ``,
        copyrightText: ``,

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        reset_email_link_text: 'Reset password',

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your API password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        blocked_account_email_title: `Blocked Access Attempt`,
        blocked_account_email_body:
            `<p
                      >Dear {{ user.name }},
                      </p>
                      <p>
                          We&nbsp;detected suspicious activity from the&nbsp;ip:: <strong>{{ user.source_ip }}</strong> from
                          <strong>{{ user.city }}</strong>, <strong>{{ user.country }}</strong>.
                      </p>
                      <p
                      >If&nbsp;that is&nbsp;your&nbsp;IP address please contact
                      <a href="https://app.evaluate.com/ux/help/contact_us.html" target="_blank" rel="noopener nofollow"
                          >Customer Success.
                          </a> Otherwise disregard this email.
                      </p>`,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Success',
        linkUrl: 'https://app.evaluate.com/ux/help/contact_us.html',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        linkText: 'Customer Success',
        linkUrl: 'https://app.evaluate.com/ux/help/contact_us.html',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Success',
        linkUrl: 'https://app.evaluate.com/ux/help/contact_us.html',
      },
      contacts: {
        phones: [
          'Evaluate HQ: 44-(0)20-7377-0800',
          'Evaluate Americas: +1-617-573-9450',
          'Evaluate APAC: +81-80-1164-4754',
        ],
        email: 'info@evaluate.com',
      },
    },
    {
      name: 'Fusion',
      themeId: THEME_IDS.COMMON,
      logoUrl: `${process.env.SSO_URL || ''}/logos/fusion-logo.svg`,
      logoBackground: '#1C2633',
      resetPassword: {
        clientServicesSection: ``,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/fusion-logo-w212.png`,
        email_footer_team_signature: '{{resolved_application_name}}',
        contactClientServiceContent:
            `<p>If&nbsp;you did not make this request, please ignore this email.</p>`,
        legalText: '',
        copyrightText: '',

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        reset_email_link_text: 'Reset password',

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your API password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        blocked_account_email_title: 'Blocked Access Attempt',
        blocked_account_email_body:
            `<p
              >Dear {{ user.name }},
              </p>
              <p>
                  We&nbsp;detected suspicious activity from the&nbsp;ip:: <strong>{{ user.source_ip }}</strong> from
                  <strong>{{ user.city }}</strong>, <strong>{{ user.country }}</strong>.
              </p>`,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Client Services',
        linkUrl: 'https://www.citeline.com/en/contact-us/',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        linkText: 'Customer Support',
        linkUrl: 'https://www.citeline.com/en/contact-us/',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Success',
        linkUrl: 'https://app.evaluate.com/ux/help/contact_us.html',
      },
      contacts: {
        phones: [
          'Americas: +1 888-670- 8900; +1 908-547- 2200',
          'EMEA: +44 (20) 337 73737',
          'APAC: +61 2 8705 6907',
        ],
        email: 'clientservices@citeline.com',
      },
    },
    {
      name: 'PharmaAPI',
      themeId: THEME_IDS.COMMON,
      logoUrl: `${process.env.SSO_URL || ''}/logos/citiline-logo-dual-color.svg`,
      logoBackground: '#14123b',
      resetPassword: {
        clientServicesSection:
            `<p>If&nbsp;you need further assistance contact 
                <a href="https://www.citeline.com/en/contact-us/" target="_blank" rel="noopener nofollow">
                  Customer Support
                </a>
              </p>`,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/citiline-logo-dual-color-w212.png`,
        email_footer_team_signature: '{{resolved_application_name}}',
        contactClientServiceContent:
            `<p
                    >If&nbsp;you did not make this request, or&nbsp;if&nbsp;you require further assistance, please contact
                    <a href="mailto:clientservices@citeline.com"
                    >Customer Support.
                    </a>
                  </p>`,
        legalText:
            `Citeline is&nbsp;owned by&nbsp;Caerus
                    US&nbsp;1, Inc.&nbsp;a&nbsp;Delaware
                    corporation with company number 6591595 (&#8220;Citeline&#8221;).
                    Citeline&rsquo;s
                    principal place of&nbsp;business is&nbsp;located at&nbsp;
                    One Pennsylvania Plaza #2505,
                    New York, NY 10119.`,
        copyrightText: `Copyright &copy;&nbsp;Citeline, a Norstella company`,

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        reset_email_link_text: 'Reset password',

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your API password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        blocked_account_email_title: 'Account locked',
        blocked_account_email_body:
            `<p
                      >Dear {{ user.name }},
                      </p>
                      <p>
                          For security reasons, your account has been locked after too many failed log in attempts.
                      </p>
                      <p
                      >Contact <a href="mailto:clientservices@citeline.com"
                          >Customer Support
                          </a> for more information and how to unlock your account.
                      </p>`,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Support',
        linkUrl: 'https://www.citeline.com/en/contact-us/',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        linkText: 'Customer Support',
        linkUrl: 'https://www.citeline.com/en/contact-us/',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Support',
        linkUrl: 'https://www.citeline.com/en/contact-us/',
      },
      contacts: {
        phones: [
          'Americas: +1 888-670- 8900; +1 908-547- 2200',
          'EMEA: +44 (20) 337 73737',
          'APAC: +61 2 8705 6907',
        ],
        email: 'clientservices@citeline.com',
      },
      customLoginText: 'To access our API Documentation SSO and non-SSO users ' +
          'need their corporate email with associated API entitlements and an API password. ' +
          'Please message <a href="mailto:clientservices@citeline.com">clientservices@citeline.com</a> ' +
          'if you are unsure or require access.',
    },
    {
      name: 'C+D',
      themeId: THEME_IDS.BLUE,
      logoUrl: `${process.env.SSO_URL || ''}/logos/cd/cd.svg`,
      logoBackground: '#ffffff',
      signup: {
        full: {
          isConsentRequired: true,
          terms:
              `<input type="checkbox" name="consent" id="consent">
            <label for="consent" class="app-terms">
    <p>
        I&nbsp;have read and understand the <a href="https://www.citeline.com/en/terms-of-use" target="_blank" rel="noopener nofollow">Terms and Conditions</a> of&nbsp;Website Use and agree to&nbsp;be&nbsp;bound by&nbsp;all of&nbsp;its terms. In&nbsp;the event the
        Customer has existing terms of&nbsp;sale with the Licensor, the existing terms of&nbsp;sale shall govern.
    </p>
    <p>By&nbsp;registering for C+D you have complete control over what we&nbsp;send you and when. View our <a href="https://www.chemistanddruggist.co.uk/more/help/contact-promise/" target="_blank" rel="noopener nofollow">customer contact promise</a>. You can amend your details at&nbsp;any time in&nbsp;My&nbsp;Account. Tick this box to&nbsp;let&nbsp;us know
        you&rsquo;ve read our <a href="https://www.norstella.com/cookie-management/" target="_blank" rel="noopener nofollow">privacy policy</a>*</p>
</label>`,
        },
        trial: {
          terms: ``,
          header: ``,
        },
      },
      resetPassword: {
        clientServicesSection: `<p>If&nbsp;you need further assistance contact 
                <a href="mailto:updatemydetails@chemistanddruggist.co.uk">
                  Customer Support
                </a>
              </p>`,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/cd/cd.png`,
        has_header_delimiter: true,
        email_footer_team_signature: '{{resolved_application_name}}',
        contactClientServiceContent:
            `<p
                    >If&nbsp;you did not make this request, or&nbsp;if&nbsp;you require further assistance, please contact
                    <a style="color:#257EA4; text-decoration: underline" href="mailto:updatemydetails@chemistanddruggist.co.uk"
                    >Customer Support.
                    </a>
                  </p>`,
        legal_text:
            `Chemist+Druggist is&nbsp;a&nbsp;trading division of&nbsp;Citeline, a&nbsp;Norstella company and operates as&nbsp;part of&nbsp;C&amp;D Intelligence U.K. Limited. C&amp;D Intelligence U.K. Limited is&nbsp;a&nbsp;company registered in&nbsp;England and Wales (Registration number 13793264) with its registered office at&nbsp;3&nbsp;More London Riverside, London SE1 2AQ`,
        copyright_text: `&copy;&nbsp;\${CURRENT_YEAR}&nbsp;Caerus US 1, Inc`,

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        reset_email_link_text: 'Reset password',

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your API password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        blocked_account_email_title: `Blocked Access Attempt`,
        blocked_account_email_body:
            `<p
                      >Dear {{ user.name }},
                      </p>
                      <p>For security reasons, your account has been locked after too many failed log in attempts.
                      </p>
                      <p
                      >Contact <a style="color:#257EA4; text-decoration: underline" href="mailto:updatemydetails@chemistanddruggist.co.uk"
                    >Customer Support</a> for more information and how to unlock your account.</p>
                      `,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Support',
        linkUrl: 'mailto:updatemydetails@chemistanddruggist.co.uk',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        linkText: 'Customer Support',
        linkUrl: 'mailto:updatemydetails@chemistanddruggist.co.uk',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Support',
        linkUrl: 'mailto:updatemydetails@chemistanddruggist.co.uk',
      },
      contacts: {
        link: {
          url: 'mailto:updatemydetails@chemistanddruggist.co.uk',
          text: 'Customer Support',
        },
      },
    },
    {
      name: 'C+D-Community',
      themeId: THEME_IDS.BLUE,
      logoUrl: `${process.env.SSO_URL || ''}/logos/cd-community/cd-community.svg`,
      logoBackground: '#ffffff',
      signup: {
        full: {
          isConsentRequired: true,
          terms:
              `<input type="checkbox" name="consent" id="consent">
            <label for="consent" class="app-terms">
    <p>
        I&nbsp;have read and understand the <a href="https://www.citeline.com/en/terms-of-use" target="_blank" rel="noopener nofollow">Terms and Conditions</a> of&nbsp;Website Use and agree to&nbsp;be&nbsp;bound by&nbsp;all of&nbsp;its terms. In&nbsp;the event the
        Customer has existing terms of&nbsp;sale with the Licensor, the existing terms of&nbsp;sale shall govern.
    </p>
    <p>By&nbsp;registering for C+D you have complete control over what we&nbsp;send you and when. View our <a href="https://www.chemistanddruggist.co.uk/more/help/contact-promise/" target="_blank" rel="noopener nofollow">customer contact promise</a>. You can amend your details at&nbsp;any time in&nbsp;My&nbsp;Account. Tick this box to&nbsp;let&nbsp;us know
        you&rsquo;ve read our <a href="https://www.norstella.com/cookie-management/" target="_blank" rel="noopener nofollow">privacy policy</a>*</p>
</label>`,
        },
        trial: {
          terms: ``,
          header: ``,
        },
      },
      resetPassword: {
        clientServicesSection: `<p>If&nbsp;you need further assistance contact 
                <a href="mailto:updatemydetails@chemistanddruggist.co.uk">
                  Customer Support
                </a>
              </p>`,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/cd-community/cd-community.png`,
        has_header_delimiter: true,
        email_footer_team_signature: '{{resolved_application_name}}',
        contactClientServiceContent:
            `<p
                    >If&nbsp;you did not make this request, or&nbsp;if&nbsp;you require further assistance, please contact
                    <a style="color:#257EA4; text-decoration: underline" href="mailto:updatemydetails@chemistanddruggist.co.uk"
                    >Customer Support.
                    </a>
                  </p>`,
        legal_text:
            `Chemist+Druggist is&nbsp;a&nbsp;trading division of&nbsp;Citeline, a&nbsp;Norstella company and operates as&nbsp;part of&nbsp;C&amp;D Intelligence U.K. Limited. C&amp;D Intelligence U.K. Limited is&nbsp;a&nbsp;company registered in&nbsp;England and Wales (Registration number 13793264) with its registered office at&nbsp;3&nbsp;More London Riverside, London SE1 2AQ`,
        copyright_text: `&copy;&nbsp;\${CURRENT_YEAR}&nbsp;Caerus US 1, Inc`,

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        reset_email_link_text: 'Reset password',

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your API password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        blocked_account_email_title: `Blocked Access Attempt`,
        blocked_account_email_body:
            `<p
                      >Dear {{ user.name }},
                      </p>
                      <p>For security reasons, your account has been locked after too many failed log in attempts.
                      </p>
                      <p
                      >Contact <a style="color:#257EA4; text-decoration: underline" href="mailto:updatemydetails@chemistanddruggist.co.uk"
                    >Customer Support</a> for more information and how to unlock your account.</p>
                      `,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Support',
        linkUrl: 'mailto:updatemydetails@chemistanddruggist.co.uk',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        linkText: 'Customer Support',
        linkUrl: 'mailto:updatemydetails@chemistanddruggist.co.uk',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Support',
        linkUrl: 'mailto:updatemydetails@chemistanddruggist.co.uk',
      },
      contacts: {
        link: {
          url: 'mailto:updatemydetails@chemistanddruggist.co.uk',
          text: 'Customer Support',
        },
      },
    },
    {
      name: 'C+D Data',
      themeId: THEME_IDS.BLUE,
      logoUrl: `${process.env.SSO_URL || ''}/logos/cd-data/cd-data.svg`,
      logoBackground: '#ffffff',
      signup: {
        full: {
          isConsentRequired: true,
          terms:
              `<input type="checkbox" name="consent" id="consent">
            <label for="consent" class="app-terms">
    <p>
        I&nbsp;have read and understand the <a href="https://www.citeline.com/en/terms-of-use" target="_blank" rel="noopener nofollow">Terms and Conditions</a> of&nbsp;Website Use and agree to&nbsp;be&nbsp;bound by&nbsp;all of&nbsp;its terms.
    </p>
    <p> Your personal information will be used in accordance with our 
    <a href="https://www.norstella.com/privacy-notice" target="_blank" rel="noopener nofollow">Privacy Notice</a>.
    We may contact you with updates, relevant promotions and information about our products.</p>
</label>`,
        },
        trial: {
          terms: ``,
          header: ``,
        },
      },
      resetPassword: {
        clientServicesSection: `<p>If&nbsp;you need further assistance contact 
                <a href="mailto:info@cddata.co.uk">
                  Customer Support
                </a>
              </p>`,
      },
      emails: {
        logoUrl: `${process.env.SSO_URL || ''}/logos/cd-data/cd-data.png`,
        has_header_delimiter: true,
        email_footer_team_signature: '{{resolved_application_name}}',
        contactClientServiceContent:
            `<p
                    >If&nbsp;you did not make this request, or&nbsp;if&nbsp;you require further assistance, please contact
                    <a style="color:#257EA4; text-decoration: underline" href="mailto:info@cddata.co.uk"
                    >Customer Support.
                    </a>
                  </p>`,
        legal_text:
            `Chemist+Druggist is&nbsp;a&nbsp;trading division of&nbsp;Citeline, a&nbsp;Norstella company and operates as&nbsp;part of&nbsp;C&amp;D Intelligence U.K. Limited. C&amp;D Intelligence U.K. Limited is&nbsp;a&nbsp;company registered in&nbsp;England and Wales (Registration number 13793264) with its registered office at&nbsp;3&nbsp;More London Riverside, London SE1 2AQ`,
        copyright_text: `&copy;&nbsp;\${CURRENT_YEAR}&nbsp;Caerus US 1, Inc`,

        verify_email_title: 'Welcome to&nbsp;{{resolved_application_name}}!',
        verify_email_body:
            `<p>You need to&nbsp;verify your email address and complete your profile to&nbsp;finish the sign up&nbsp;process.</p>
           <p>This link will expire in&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.</p>`,

        welcome_reset_email_link_text: 'Log in now',

        welcome_reset_email_title_regular_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p>You are now registered to the <a href="http://www.cddata.co.uk" style="color:#257EA4;">C+D Data site</a>.
            </p>
            <p>Your username is: {{ user.email }}
            </p>
            <p>
				 To complete your registration please&nbsp;\${EMAIL_VERIFY_TTL_HOURS}&nbsp;click this link to create a password for the site.
           </p>
			 <p>
           <strong>Questions?</strong><br />
				 If you have any questions on how to access the site please email us on <a href="mailto:info@cddata.co.uk" style="color:#257EA4;">info@cddata.co.uk</a><br />
				    <br />
				  </p>`,

        welcome_reset_email_title_api_user: 'Welcome to&nbsp;{{resolved_application_name}}!',
        welcome_reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >You will need to&nbsp;set a&nbsp;password when you first log&nbsp;in.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
           </p>`,

        reset_email_link_text: 'Reset password',

        reset_email_title_regular_user: 'Forgot your password?',
        reset_email_body_regular_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >Thank you for requesting a new password
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
             Please &nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;click this link and you will be redirected to a page where you can create a new password:</p>
            </p>
           <p>
           <strong>Questions?</strong><br />
				 If you have any questions on how to access the site please email us on <a href="mailto:info@cddata.co.uk" style="color:#257EA4;">info@cddata.co.uk</a><br />
				    <br />
				  </p>`,

        reset_email_title_api_user: 'Forgot your API password?',
        reset_email_body_api_user:
            `<p
            >Dear {{ user.name }},
            </p>
            <p
            >We&nbsp;have received a&nbsp;request to&nbsp;reset your API password.
            </p>
            <p
            >This link will expire in&nbsp;\${EMAIL_RESET_PASSWORD_TTL_HOURS}&nbsp;hours and can be&nbsp;used only once.
            </p>`,

        blocked_account_email_title: `Blocked Access Attempt`,
        blocked_account_email_body:
            `<p
                      >Dear {{ user.name }},
                      </p>
                      <p>For security reasons, your account has been locked after too many failed log in attempts.
                      </p>
                      <p
                      >Contact <a style="color:#257EA4; text-decoration: underline" href="mailto:info@cddata.co.uk"
                    >Customer Support</a> for more information and how to unlock your account.</p>
                      `,
      },
      verifyFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Support',
        linkUrl: 'mailto:info@cddata.co.uk',
      },
      errorFooter: {
        text: 'Try again, if the problem persists or you need further assistance contact',
        linkText: 'Customer Support',
        linkUrl: 'mailto:info@cddata.co.uk',
      },
      fedSignupErrorFooter: {
        text: 'If you need further assistance contact our',
        linkText: 'Customer Support',
        linkUrl: 'mailto:info@cddata.co.uk',
      },
      contacts: {
        link: {
          url: 'mailto:info@cddata.co.uk',
          text: 'Customer Support',
        },
      },
    },
  ],
  themes: [
    {
      id: THEME_IDS.COMMON,
      cssVariables: {
        [AVAILABLE_THEME_VARIABLES.PRIMARY_COLOR]: '#0f847c',
        [AVAILABLE_THEME_VARIABLES.BASE_FOCUS_COLOR]: '#0f847c',
        [AVAILABLE_THEME_VARIABLES.LINK_COLOR]: '#0f847c',
        [AVAILABLE_THEME_VARIABLES.SUCCESS_COLOR]: '#0f847c',
      },
    },
    {
      id: THEME_IDS.BLUE,
      cssVariables: {
        [AVAILABLE_THEME_VARIABLES.PRIMARY_COLOR]: '#257EA4',
        [AVAILABLE_THEME_VARIABLES.BASE_FOCUS_COLOR]: '#257EA4',
        [AVAILABLE_THEME_VARIABLES.LINK_COLOR]: '#257EA4',
        [AVAILABLE_THEME_VARIABLES.SUCCESS_COLOR]: '#257EA4',
      },
    },
  ],
  applicatons: [
    {
      code: 'citeline-news-and-insights',
      emailVerify: {
        seo: {
          title: 'Check your email | News and Insights',
        },
        title: 'Check Your Inbox to Verify Your Email',
        description: `<p>We&rsquo;ve sent a&nbsp;confirmation email&nbsp;to {{user.email}}. Click the link in&nbsp;the email to&nbsp;continue.</p>
                      <p>If&nbsp;you don&rsquo;t see&nbsp;it, check your spam folder or&nbsp;click below to&nbsp;resend.</p>`,
      },
      signupFinish: {
        seo: {
          title: 'Complete Your Profile | News and Insights',
        },
        subHeader: {
          title: 'Complete Your Profile',
          description: `<p>Tell&nbsp;us a&nbsp;little more about you so&nbsp;we&nbsp;can tailor your experience.</p>
               <p>You&rsquo;ll also receive our daily newsletter, bringing you the latest industry insights, plus breaking news alerts. You can update your preferences anytime.</p>`,
        },
      },
      pages: {
        [KNOWN_PAGES.RESET_PASSWORD_REQUEST]: {
          seo: {
            title: 'Forgot your password? | News and Insights',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD]: {
          seo: {
            title: 'Set up your password | News and Insights',
          },
        },
        [KNOWN_PAGES.SIGNUP_ID]: {
          seo: {
            title: 'Sign up | News and Insights',
          },
        },
      }
    },
    {
      code: 'citeline-news-and-insights-pink-sheet-trial',
      emailVerify: {
        seo: {
          title: 'Verify Your Email | Pink Sheet',
        },
        title: 'Check Your Inbox to Verify Your Email',
        description: `<p>We&rsquo;ve sent a&nbsp;confirmation email&nbsp;to {{user.email}}. Click the link in&nbsp;the email to&nbsp;continue.</p>
                      <p>If&nbsp;you don&rsquo;t see&nbsp;it, check your spam folder or&nbsp;click below to&nbsp;resend.</p>`,
      },
      signupFinishTrial: {
        seo: {
          title: 'Complete Your Profile | Pink Sheet',
        },
        subHeader: {
          title: 'Complete Your Profile',
          description: `<p>Tell&nbsp;us a&nbsp;little more about you so&nbsp;we&nbsp;can tailor your experience.</p>
               <p>You&rsquo;ll also receive our daily newsletter, bringing you the latest industry insights, plus breaking news alerts. You can update your preferences anytime.</p>`,
        },
      },
      pages: {
        [KNOWN_PAGES.LOGIN_ID]: {
          seo: {
            title: 'Login | Pink Sheet | News and Insights',
          },
        },
        [KNOWN_PAGES.LOGIN_PASSWORD]: {
          seo: {
            title: 'Login | Pink Sheet | News and Insights',
          },
        },
        [KNOWN_PAGES.SIGNUP_ID]: {
          seo: {
            title: 'Start Your Free Trial | Pink Sheet',
          },
          form: {
            title: 'Start Your Free Trial for Pink Sheet',
            description: 'Unlock expert pharma policy & regulatory news and analysis. Enter your email to get started.',
          },
        },
        [KNOWN_PAGES.SIGNUP_PASSWORD]: {
          seo: {
            title: 'Create Your Account | Pink Sheet',
          },
          form: {
            title: 'Secure Your Account',
            description: 'Create a password to access your free trial.',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_REQUEST]: {
          seo: {
            title: 'Forget your password? | News and Insights',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_EMAIL]: {
          seo: {
            title: 'Check your email | Pink Sheet | News and Insights',
          },
          subDescription: 'If you don\'t see it, check your spam folder or click below to resend.',
        },
        [KNOWN_PAGES.RESET_PASSWORD]: {
          seo: {
            title: 'Set up your password | News and Insights',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_SUCCESS]: {
          seo: {
            title: 'Password set up successful | Pink Sheet | News and Insights',
          },
        },
        [KNOWN_PAGES.PASSKEY_ENROLLMENT]: {
          seo: {
            title: 'Create a passkey | Pink Sheet | News and Insights',
          },
        },
      },
    },
    {
      code: 'citeline-news-and-insights-generics-bulletin-trial',
      emailVerify: {
        seo: {
          title: 'Verify Your Email | Generics Bulletin',
        },
        title: 'Check Your Inbox to Verify Your Email',
        description: `<p>We&rsquo;ve sent a&nbsp;confirmation email&nbsp;to {{user.email}}. Click the link in&nbsp;the email to&nbsp;continue.</p>
                      <p>If&nbsp;you don&rsquo;t see&nbsp;it, check your spam folder or&nbsp;click below to&nbsp;resend.</p>`,
      },
      signupFinishTrial: {
        seo: {
          title: 'Complete Your Profile | Generics Bulletin',
        },
        subHeader: {
          title: 'Complete Your Profile',
          description: `<p>Tell&nbsp;us a&nbsp;little more about you so&nbsp;we&nbsp;can tailor your experience.</p>
               <p>You&rsquo;ll also receive our daily newsletter, bringing you the latest industry insights, plus breaking news alerts. You can update your preferences anytime.</p>`,
        },
      },
      pages: {
        [KNOWN_PAGES.LOGIN_ID]: {
          seo: {
            title: 'Login | Generics Bulletin | News and Insights',
          },
        },
        [KNOWN_PAGES.LOGIN_PASSWORD]: {
          seo: {
            title: 'Login | Generics Bulletin | News and Insights',
          },
        },
        [KNOWN_PAGES.SIGNUP_ID]: {
          seo: {
            title: 'Start Your Free Trial | Generics Bulletin',
          },
          form: {
            title: 'Start Your Free Trial for Generics Bulletin',
            description: 'Unlock in-depth news & analysis of generics, biosimilars & off-patent medicines. Enter your email address to get started.',
          },
        },
        [KNOWN_PAGES.SIGNUP_PASSWORD]: {
          seo: {
            title: 'Create Your Account | Generics Bulletin',
          },
          form: {
            title: 'Secure Your Account',
            description: 'Create a password to access your free trial.',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_REQUEST]: {
          seo: {
            title: 'Forget your password? | News and Insights',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_EMAIL]: {
          seo: {
            title: 'Check your email | Generics Bulletin | News and Insights',
          },
          subDescription: 'If you don\'t see it, check your spam folder or click below to resend.',
        },
        [KNOWN_PAGES.RESET_PASSWORD]: {
          seo: {
            title: 'Set up your Password | Generics Bulletin',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_SUCCESS]: {
          seo: {
            title: 'Password set up successful | Generics Bulletin | News and Insights',
          },
        },
        [KNOWN_PAGES.PASSKEY_ENROLLMENT]: {
          seo: {
            title: 'Create a passkey | Generics Bulletin | News and Insights',
          },
        },
      },
    },
    {
      code: 'citeline-news-and-insights-hbw-insight-trial',
      emailVerify: {
        seo: {
          title: 'Verify Your Email | HBW Insight',
        },
        title: 'Check Your Inbox to Verify Your Email',
        description: `<p>We&rsquo;ve sent a&nbsp;confirmation email&nbsp;to {{user.email}}. Click the link in&nbsp;the email to&nbsp;continue.</p>
                      <p>If&nbsp;you don&rsquo;t see&nbsp;it, check your spam folder or&nbsp;click below to&nbsp;resend.</p>`,
      },
      signupFinishTrial: {
        seo: {
          title: 'Complete Your Profile | HBW Insight',
        },
        subHeader: {
          title: 'Complete Your Profile',
          description: `<p>Tell&nbsp;us a&nbsp;little more about you so&nbsp;we&nbsp;can tailor your experience.</p>
               <p>You&rsquo;ll also receive our daily newsletter, bringing you the latest industry insights, plus breaking news alerts. You can update your preferences anytime.</p>`,
        },
      },
      pages: {
        [KNOWN_PAGES.LOGIN_ID]: {
          seo: {
            title: 'Login | HBW Insight | News and Insights',
          },
        },
        [KNOWN_PAGES.LOGIN_PASSWORD]: {
          seo: {
            title: 'Login | HBW Insight | News and Insights',
          },
        },
        [KNOWN_PAGES.SIGNUP_ID]: {
          seo: {
            title: 'Start Your Free Trial | HBW Insight',
          },
          form: {
            title: 'Start Your Free Trial for HBW Insight',
            description: 'In-depth consumer health news and analysis across health, beauty & wellness. Enter your email to get started.',
          },
        },
        [KNOWN_PAGES.SIGNUP_PASSWORD]: {
          seo: {
            title: 'Create Your Account | HBW Insight',
          },
          form: {
            title: 'Secure Your Account',
            description: 'Create a password to access your free trial.',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_REQUEST]: {
          seo: {
            title: 'Forget your password? | News and Insights',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_EMAIL]: {
          seo: {
            title: 'Check your email | HBW Insight | News and Insights',
          },
          subDescription: 'If you don\'t see it, check your spam folder or click below to resend.',
        },
        [KNOWN_PAGES.RESET_PASSWORD]: {
          seo: {
            title: 'Set up your Password | HBW Insight',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_SUCCESS]: {
          seo: {
            title: 'Password set up successful | HBW Insight | News and Insights',
          },
        },
        [KNOWN_PAGES.PASSKEY_ENROLLMENT]: {
          seo: {
            title: 'Create a passkey | HBW Insight | News and Insights',
          },
        },
      },
    },
    {
      code: 'citeline-news-and-insights-in-vivo-trial',
      emailVerify: {
        seo: {
          title: 'Verify Your Email | In Vivo',
        },
        title: 'Check Your Inbox to Verify Your Email',
        description: `<p>We&rsquo;ve sent a&nbsp;confirmation email&nbsp;to {{user.email}}. Click the link in&nbsp;the email to&nbsp;continue.</p>
                      <p>If&nbsp;you don&rsquo;t see&nbsp;it, check your spam folder or&nbsp;click below to&nbsp;resend.</p>`,
      },
      signupFinishTrial: {
        seo: {
          title: 'Complete Your Profile | In Vivo',
        },
        subHeader: {
          title: 'Complete Your Profile',
          description: `<p>Tell&nbsp;us a&nbsp;little more about you so&nbsp;we&nbsp;can tailor your experience.</p>
               <p>You&rsquo;ll also receive our daily newsletter, bringing you the latest industry insights, plus breaking news alerts. You can update your preferences anytime.</p>`,
        },
      },
      pages: {
        [KNOWN_PAGES.LOGIN_ID]: {
          seo: {
            title: 'Login | In Vivo | News and Insights',
          },
        },
        [KNOWN_PAGES.LOGIN_PASSWORD]: {
          seo: {
            title: 'Login | In Vivo | News and Insights',
          },
        },
        [KNOWN_PAGES.SIGNUP_ID]: {
          seo: {
            title: 'Start Your Free Trial | In Vivo',
          },
          form: {
            title: 'Start Your Free Trial for In Vivo',
            description: 'Unlock in-depth life sciences journalism and strategic analysis. Enter your email to get started.',
          },
        },
        [KNOWN_PAGES.SIGNUP_PASSWORD]: {
          seo: {
            title: 'Create Your Account | In Vivo',
          },
          form: {
            title: 'Secure Your Account',
            description: 'Create a password to access your free trial.',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_REQUEST]: {
          seo: {
            title: 'Forget your password? | News and Insights',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_EMAIL]: {
          seo: {
            title: 'Check your email | In Vivo | News and Insights',
          },
          subDescription: 'If you don\'t see it, check your spam folder or click below to resend.',
        },
        [KNOWN_PAGES.RESET_PASSWORD]: {
          seo: {
            title: 'Set up your Password | In Vivo',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_SUCCESS]: {
          seo: {
            title: 'Password set up successful | In Vivo | News and Insights',
          },
        },
        [KNOWN_PAGES.PASSKEY_ENROLLMENT]: {
          seo: {
            title: 'Create a passkey | In Vivo | News and Insights',
          },
        },
      },
    },
    {
      code: 'citeline-news-and-insights-medtech-insight-trial',
      emailVerify: {
        seo: {
          title: 'Verify Your Email | Medtech Insight',
        },
        title: 'Check Your Inbox to Verify Your Email',
        description: `<p>We&rsquo;ve sent a&nbsp;confirmation email&nbsp;to {{user.email}}. Click the link in&nbsp;the email to&nbsp;continue.</p>
                      <p>If&nbsp;you don&rsquo;t see&nbsp;it, check your spam folder or&nbsp;click below to&nbsp;resend.</p>`,
      },
      signupFinishTrial: {
        seo: {
          title: 'Complete Your Profile | Medtech Insight',
        },
        subHeader: {
          title: 'Complete Your Profile',
          description: `<p>Tell&nbsp;us a&nbsp;little more about you so&nbsp;we&nbsp;can tailor your experience.</p>
               <p>You&rsquo;ll also receive our daily newsletter, bringing you the latest industry insights, plus breaking news alerts. You can update your preferences anytime.</p>`,
        },
      },
      pages: {
        [KNOWN_PAGES.LOGIN_ID]: {
          seo: {
            title: 'Login | Medtech Insight | News and Insights',
          },
        },
        [KNOWN_PAGES.LOGIN_PASSWORD]: {
          seo: {
            title: 'Login | Medtech Insight | News and Insights',
          },
        },
        [KNOWN_PAGES.SIGNUP_ID]: {
          seo: {
            title: 'Start Your Free Trial | Medtech Insight',
          },
          form: {
            title: 'Start Your Free Trial for Medtech Insight',
            description: 'Unlock commercial & regulatory news & analysis on the medtech market. Enter your email to get started.',
          },
        },
        [KNOWN_PAGES.SIGNUP_PASSWORD]: {
          seo: {
            title: 'Create Your Account | Medtech Insight',
          },
          form: {
            title: 'Secure Your Account',
            description: 'Create a password to access your free trial.',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_REQUEST]: {
          seo: {
            title: 'Forget your password? | News and Insights',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_EMAIL]: {
          seo: {
            title: 'Check your email | Medtech Insight | News and Insights',
          },
          subDescription: 'If you don\'t see it, check your spam folder or click below to resend.',
        },
        [KNOWN_PAGES.RESET_PASSWORD]: {
          seo: {
            title: 'Set up your Password | Medtech Insight',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_SUCCESS]: {
          seo: {
            title: 'Password set up successful | Medtech Insight | News and Insights',
          },
        },
        [KNOWN_PAGES.PASSKEY_ENROLLMENT]: {
          seo: {
            title: 'Create a passkey | Medtech Insight | News and Insights',
          },
        },
      },
    },
    {
      code: 'citeline-news-and-insights-scrip-trial',
      emailVerify: {
        seo: {
          title: 'Verify Your Email | Scrip',
        },
        title: 'Check Your Inbox to Verify Your Email',
        description: `<p>We&rsquo;ve sent a&nbsp;confirmation email&nbsp;to {{user.email}}. Click the link in&nbsp;the email to&nbsp;continue.</p>
                      <p>If&nbsp;you don&rsquo;t see&nbsp;it, check your spam folder or&nbsp;click below to&nbsp;resend.</p>`,
      },
      signupFinishTrial: {
        seo: {
          title: 'Complete Your Profile | Scrip',
        },
        subHeader: {
          title: 'Complete Your Profile',
          description: `<p>Tell&nbsp;us a&nbsp;little more about you so&nbsp;we&nbsp;can tailor your experience.</p>
               <p>You&rsquo;ll also receive our daily newsletter, bringing you the latest industry insights, plus breaking news alerts. You can update your preferences anytime.</p>`,
        },
      },
      pages: {
        [KNOWN_PAGES.LOGIN_ID]: {
          seo: {
            title: 'Login | Scrip | News and Insights',
          },
        },
        [KNOWN_PAGES.LOGIN_PASSWORD]: {
          seo: {
            title: 'Login | Scrip | News and Insights',
          },
        },
        [KNOWN_PAGES.SIGNUP_ID]: {
          seo: {
            title: 'Start Your Free Trial | Scrip',
          },
          form: {
            title: 'Start Your Free Trial for Scrip',
            description: 'Unlock in-depth commercial biopharma news & analysis. Enter your email to get started.',
          },
        },
        [KNOWN_PAGES.SIGNUP_PASSWORD]: {
          seo: {
            title: 'Create Your Account | Scrip',
          },
          form: {
            title: 'Secure Your Account',
            description: 'Create a password to access your free trial.',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_REQUEST]: {
          seo: {
            title: 'Forget your password? | News and Insights',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_EMAIL]: {
          seo: {
            title: 'Check your email | Scrip | News and Insights',
          },
          subDescription: 'If you don\'t see it, check your spam folder or click below to resend.',
        },
        [KNOWN_PAGES.RESET_PASSWORD]: {
          seo: {
            title: 'Set up your Password | Scrip',
          },
        },
        [KNOWN_PAGES.RESET_PASSWORD_SUCCESS]: {
          seo: {
            title: 'Password set up successful | Scrip | News and Insights',
          },
        },
        [KNOWN_PAGES.PASSKEY_ENROLLMENT]: {
          seo: {
            title: 'Create a passkey | Scrip | News and Insights',
          },
        },
      },
    },
  ],
}

module.exports = config
